/**
 * Phase F2 + F3 — Tasks & Follow-Ups
 * Replaces the legacy FollowUps.jsx with a 2-tab (Customer | Internal) experience,
 * dashboard widgets, calendar view (react-big-calendar), reports tab, and rich actions.
 *
 * Backward-compatible — uses /api/sales/follow-ups (legacy list/CRUD) +
 * /api/sales/follow-ups/* additive endpoints from Phase F1.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, Loader2, Search, Send, Clock, X, RefreshCw, Sparkles, AlertTriangle, FileText,
  Paperclip, MoreHorizontal, CheckCircle, Snowflake, Trash2, Mail, MessageCircle, Bell,
  Calendar as CalIcon, BarChart3, List, TrendingUp, Users, Activity, Zap,
} from "lucide-react";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addDays } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";

import {
  TASK_TYPES_CUSTOMER, TASK_TYPES_INTERNAL, PRIORITY_META, STATUS_COLORS, SNOOZE_PRESETS,
  PriorityBadge, TaskTypeChip,
  DashboardWidgets, OutcomeModal, NextFollowUpModal, AISuggestModal,
  NotesTimelinePanel, AttachmentUploader, QuickTemplateChips, DeliveryDetailsPanel,
} from "./TasksFollowUpsWidgets";

const VIEWS = [
  { id: "list",     label: "List",     Icon: List },
  { id: "calendar", label: "Calendar", Icon: CalIcon },
  { id: "reports",  label: "Reports",  Icon: BarChart3 },
];

const locales = { "en-US": undefined };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

export default function TasksFollowUps() {
  const [tab, setTab] = useState("customer");           // customer | internal
  const [view, setView] = useState("list");             // list | calendar | reports
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createTemplate, setCreateTemplate] = useState(null);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [nextFor, setNextFor] = useState(null);
  const [aiSuggestFor, setAiSuggestFor] = useState(null);
  const [timelineFor, setTimelineFor] = useState(null);
  const [attachFor, setAttachFor] = useState(null);
  const [snoozeFor, setSnoozeFor] = useState(null);
  const [deliveryFor, setDeliveryFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    q.set("status", filterStatus === "all" ? "all" : filterStatus);
    if (mineOnly) q.set("assigned_to", "me");
    api.get(`/sales/follow-ups?${q.toString()}`)
      .then(r => {
        // Tab filter (client-side because legacy endpoint doesn't filter by kind)
        let list = r.data || [];
        list = list.filter(x => {
          const k = x.kind || "customer";
          if (tab === "internal") return k === "internal";
          return k !== "internal";
        });
        if (filterPriority !== "all") list = list.filter(x => (x.priority || "medium") === filterPriority);
        if (filterType !== "all") list = list.filter(x => (x.task_type || "custom") === filterType);
        if (search.trim()) {
          const s = search.toLowerCase();
          list = list.filter(x =>
            (x.title || "").toLowerCase().includes(s) ||
            (x.note || "").toLowerCase().includes(s) ||
            (x.lead_id || "").toLowerCase().includes(s) ||
            (x.task_type || "").toLowerCase().includes(s)
          );
        }
        setRows(list);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [filterStatus, mineOnly, tab, filterPriority, filterType, search]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // ---- Quick actions ----
  const markDone = async (fid) => {
    try { await api.post(`/sales/follow-ups/${fid}/done`); toast.success("Marked done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendNow = async (fid) => {
    try { await api.post(`/sales/follow-ups/${fid}/send-now`); toast.success("Sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const snooze = async (fid, hours) => {
    try {
      await api.post(`/sales/follow-ups/${fid}/snooze`, { hours });
      toast.success(`Snoozed`);
      setSnoozeFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancel = async (fid) => {
    if (!window.confirm("Cancel this task?")) return;
    try { await api.delete(`/sales/follow-ups/${fid}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onCreateApply = (fu) => { setShowCreate(false); setCreateTemplate(null); load(); };

  return (
    <div className="space-y-4" data-testid="tasks-followups-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Tasks & Follow-Ups</h2>
          <p className="text-[11px] text-gray-500">Single engine · customer follow-ups + internal tasks · live updates</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5">
            {VIEWS.map(v => {
              const I = v.Icon;
              return (
                <button key={v.id} onClick={() => setView(v.id)} data-testid={`view-${v.id}`}
                  className={`px-2.5 py-1 text-xs font-bold rounded-md inline-flex items-center gap-1 ${view === v.id ? "bg-[#1a3c5e] text-white" : "text-gray-600 hover:bg-gray-50"}`}>
                  <I className="w-3 h-3" /> {v.label}
                </button>
              );
            })}
          </div>
          <button onClick={() => { setCreateTemplate(null); setShowCreate(true); }}
            data-testid="fu-create-btn"
            className="inline-flex items-center gap-1.5 bg-[#F7941D] hover:bg-[#e0861a] text-white px-3 py-2 rounded-lg text-sm font-bold">
            <Plus className="w-4 h-4" /> New Task
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button onClick={() => setTab("customer")} data-testid="tab-customer"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-all ${tab === "customer" ? "border-[#F7941D] text-[#1a3c5e]" : "border-transparent text-gray-500 hover:text-[#1a3c5e]"}`}>
          Customer Follow-Ups
        </button>
        <button onClick={() => setTab("internal")} data-testid="tab-internal"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-all ${tab === "internal" ? "border-[#F7941D] text-[#1a3c5e]" : "border-transparent text-gray-500 hover:text-[#1a3c5e]"}`}>
          Internal Tasks
        </button>
      </div>

      {/* Dashboard widgets */}
      <DashboardWidgets kind={tab} onWidgetClick={w => {
        if (w === "overdue") { setFilterStatus("pending"); setFilterPriority("all"); }
        if (w === "urgent") { setFilterPriority("urgent"); setFilterStatus("pending"); }
        if (w === "completed_today") { setFilterStatus("done"); setFilterPriority("all"); }
        if (w === "missed") { setFilterStatus("failed"); setFilterPriority("all"); }
        if (["due_today", "tomorrow", "this_week", "pending"].includes(w)) {
          setFilterStatus("pending");
        }
      }} />

      {/* Quick template chips */}
      <QuickTemplateChips kind={tab} onPick={t => { setCreateTemplate(t); setShowCreate(true); }} />

      {view === "list" && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search task, lead, note…" data-testid="fu-search"
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              data-testid="fu-filter-status"
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
              <option value="all">All status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="done">Done</option>
              <option value="failed">Failed</option>
              <option value="snoozed">Snoozed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
              data-testid="fu-filter-priority"
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
              <option value="all">All priorities</option>
              {Object.keys(PRIORITY_META).map(p => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              data-testid="fu-filter-type"
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
              <option value="all">All types</option>
              {(tab === "internal" ? TASK_TYPES_INTERNAL : TASK_TYPES_CUSTOMER).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} data-testid="fu-mine-only" />
              Assigned to me
            </label>
            <button onClick={load} className="px-2 py-1.5 text-xs border border-gray-200 rounded inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          {/* Rows */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="fu-list">
            {loading && <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>}
            {!loading && rows.length === 0 && <div className="text-center py-10 text-gray-400 text-sm">No tasks match these filters.</div>}
            {!loading && rows.map(r => (
              <TaskRow
                key={r.id} row={r} tab={tab}
                onDone={() => markDone(r.id)}
                onSendNow={() => sendNow(r.id)}
                onSnooze={() => setSnoozeFor(r)}
                onOutcome={() => setOutcomeFor(r)}
                onNext={() => setNextFor(r)}
                onAISuggest={() => r.lead_id && setAiSuggestFor(r.lead_id)}
                onTimeline={() => setTimelineFor(r)}
                onAttach={() => setAttachFor(r)}
                onDelivery={() => setDeliveryFor(r)}
                onCancel={() => cancel(r.id)}
              />
            ))}
          </div>
        </>
      )}

      {view === "calendar" && <CalendarView rows={rows} tab={tab} onSelect={r => setTimelineFor(r)} onRefresh={load} />}

      {view === "reports" && <ReportsView tab={tab} />}

      {/* Modals */}
      {showCreate && (
        <CreateTaskModal
          tab={tab}
          template={createTemplate}
          onClose={() => { setShowCreate(false); setCreateTemplate(null); }}
          onSaved={onCreateApply}
        />
      )}
      {outcomeFor && <OutcomeModal followUp={outcomeFor} onClose={() => setOutcomeFor(null)} onSaved={load} />}
      {nextFor && <NextFollowUpModal followUp={nextFor} onClose={() => setNextFor(null)} onSaved={load} />}
      {aiSuggestFor && (
        <AISuggestModal leadId={aiSuggestFor} onClose={() => setAiSuggestFor(null)}
          onApply={(s) => {
            setAiSuggestFor(null);
            setCreateTemplate({
              suggestion: { ...s, lead_id: aiSuggestFor },
              name: `AI: ${s.task_type}`,
              kind: "customer",
              task_type: s.task_type,
              default_priority: s.priority,
              channels: ["in_app", "whatsapp", "email"],
              default_offset_hours: (s.in_days || 2) * 24,
              body: s.note || "",
            });
            setShowCreate(true);
          }} />
      )}
      {timelineFor && <NotesTimelinePanel followUp={timelineFor} onClose={() => setTimelineFor(null)} onUpdated={load} />}
      {attachFor && <AttachmentUploader followUp={attachFor} onClose={() => setAttachFor(null)} onSaved={load} />}
      {deliveryFor && <DeliveryDetailsPanel followUp={deliveryFor} onClose={() => setDeliveryFor(null)} />}
      {snoozeFor && (
        <SnoozePicker onClose={() => setSnoozeFor(null)} onPick={(h) => snooze(snoozeFor.id, h)} />
      )}
    </div>
  );
}

/* =================== Task Row =================== */

function TaskRow({ row, tab, onDone, onSendNow, onSnooze, onOutcome, onNext, onAISuggest, onTimeline, onAttach, onDelivery, onCancel }) {
  const kind = row.kind || "customer";
  const scheduled = row.scheduled_at ? new Date(row.scheduled_at) : null;
  const overdue = scheduled && scheduled < new Date() && row.status === "pending";
  // Phase F1 (iter 36) — quick visual indicator of last delivery result
  const cr = row.channel_results || row.results || {};
  const deliveredChannels = row.delivered_channels || Object.entries(cr).filter(([, v]) => v && ["sent", "delivered", "read"].includes(v.status)).map(([k]) => k);
  const failedChannels = Object.entries(cr).filter(([, v]) => v && v.status === "failed").map(([k]) => k);

  return (
    <div data-testid={`fu-row-${row.id}`} className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 flex items-center gap-3 ${overdue ? "border-l-4 border-l-rose-500" : ""}`}>
      <div className="flex flex-col items-center gap-1 flex-shrink-0 w-16">
        <div className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${STATUS_COLORS[row.status] || STATUS_COLORS.pending}`}>
          {row.status || "pending"}
        </div>
        <PriorityBadge priority={row.priority || "medium"} size="xs" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
          <TaskTypeChip taskType={row.task_type || "custom"} kind={kind} />
          {row.escalation_level > 0 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded bg-rose-100 text-rose-700 border border-rose-300">
              <AlertTriangle className="w-2.5 h-2.5" /> ESC {row.escalation_level}
            </span>
          )}
          {row.attempt_no > 1 && (
            <span className="text-[10px] text-gray-500">attempt {row.attempt_no}</span>
          )}
        </div>
        <div className="text-sm font-bold text-[#1a3c5e] truncate">
          {row.title || row.note?.slice(0, 80) || row.task_type || "Task"}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
          <Clock className="w-3 h-3" />
          {scheduled ? scheduled.toLocaleString() : "—"}
          {overdue && <span className="text-rose-600 font-bold">· OVERDUE</span>}
          {row.lead_id && <span className="truncate">· lead {String(row.lead_id).slice(0, 8)}</span>}
          {row.assigned_to_name && <span>· @{row.assigned_to_name}</span>}
          {(row.notes_history || []).length > 0 && <span>· {(row.notes_history || []).length} note(s)</span>}
          {(row.attachments || []).length > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip className="w-2.5 h-2.5" /> {(row.attachments || []).length}</span>}
          {deliveredChannels.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-emerald-700 font-bold" title="Delivered channels">
              · <CheckCircle className="w-2.5 h-2.5" /> {deliveredChannels.join("+")}
            </span>
          )}
          {failedChannels.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-rose-700 font-bold" title="Failed channels">
              · <AlertTriangle className="w-2.5 h-2.5" /> {failedChannels.join("+")}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1 flex-shrink-0">
        {row.status === "pending" && kind === "customer" && (
          <button onClick={onSendNow} data-testid={`fu-send-${row.id}`} title="Send now"
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
            <Send className="w-3.5 h-3.5" />
          </button>
        )}
        {row.status !== "done" && row.status !== "cancelled" && (
          <button onClick={onOutcome} data-testid={`fu-outcome-btn-${row.id}`} title="Record outcome / complete"
            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded">
            <CheckCircle className="w-3.5 h-3.5" />
          </button>
        )}
        {row.status === "pending" && (
          <button onClick={onSnooze} data-testid={`fu-snooze-${row.id}`} title="Snooze"
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded">
            <Snowflake className="w-3.5 h-3.5" />
          </button>
        )}
        {kind === "customer" && row.lead_id && (
          <button onClick={onAISuggest} data-testid={`fu-ai-${row.id}`} title="AI suggestions"
            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded">
            <Sparkles className="w-3.5 h-3.5" />
          </button>
        )}
        {row.status === "done" && (
          <button onClick={onNext} data-testid={`fu-next-${row.id}`} title="Create next follow-up"
            className="p-1.5 text-orange-600 hover:bg-orange-50 rounded">
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={onAttach} data-testid={`fu-attach-${row.id}`} title="Attach file"
          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded">
          <Paperclip className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelivery} data-testid={`fu-delivery-${row.id}`} title="Delivery details + Test execute"
          className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded">
          <Activity className="w-3.5 h-3.5" />
        </button>
        <button onClick={onTimeline} data-testid={`fu-timeline-${row.id}`} title="Activity timeline"
          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded">
          <Clock className="w-3.5 h-3.5" />
        </button>
        {row.status === "pending" && (
          <button onClick={onCancel} data-testid={`fu-cancel-${row.id}`} title="Cancel"
            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/* =================== Snooze Picker =================== */

function SnoozePicker({ onClose, onPick }) {
  const [customHours, setCustomHours] = useState(2);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="fu-snooze-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-1.5"><Snowflake className="w-4 h-4" /> Snooze</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 space-y-2">
          <div className="grid grid-cols-2 gap-1.5">
            {SNOOZE_PRESETS.map(p => (
              <button key={p.label} onClick={() => onPick(p.hours)} data-testid={`fu-snooze-${p.label.replace(/\s/g, "-").toLowerCase()}`}
                className="px-2 py-1.5 text-xs font-bold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg">
                {p.label}
              </button>
            ))}
          </div>
          <div className="border-t pt-2 mt-2 space-y-1">
            <label className="text-[10px] uppercase font-bold text-gray-600">Custom (hours)</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" value={customHours} onChange={e => setCustomHours(Number(e.target.value))}
                data-testid="fu-snooze-custom-hours" className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm" />
              <button onClick={() => onPick(customHours)} data-testid="fu-snooze-custom-apply"
                className="px-3 py-1.5 bg-[#1a3c5e] text-white text-xs font-bold rounded">Apply</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =================== Create Task Modal =================== */

function CreateTaskModal({ tab, template, onClose, onSaved }) {
  const isInternal = tab === "internal";
  const allTypes = isInternal ? TASK_TYPES_INTERNAL : TASK_TYPES_CUSTOMER;
  const sug = template?.suggestion || null;

  const defaultDate = useMemo(() => {
    const offsetH = template?.default_offset_hours ?? (sug ? (sug.in_days || 2) * 24 : 24);
    return new Date(Date.now() + offsetH * 3600 * 1000).toISOString().slice(0, 16);
  }, [template, sug]);

  const [kind] = useState(isInternal ? "internal" : "customer");
  const [taskType, setTaskType] = useState(template?.task_type || (isInternal ? "general_task" : "call"));
  const [priority, setPriority] = useState(template?.default_priority || sug?.priority || "medium");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState(sug?.note || template?.body || "");
  const [scheduledAt, setScheduledAt] = useState(defaultDate);
  const [leadId, setLeadId] = useState(sug?.lead_id || "");
  const [channels, setChannels] = useState(template?.channels || ["in_app", "whatsapp", "email"]);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    if (isInternal) return;
    api.get("/leads?limit=200").then(r => setLeads(r.data || [])).catch(() => setLeads([]));
  }, [isInternal]);

  const toggleChannel = (c) => {
    setChannels(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  const submit = async () => {
    if (!isInternal && !leadId) { toast.error("Select a lead for customer tasks"); return; }
    if (!scheduledAt) { toast.error("Pick a scheduled date/time"); return; }
    setSaving(true);
    try {
      await api.post("/sales/follow-ups/create", {
        kind, task_type: taskType, title: title || null, note,
        priority,
        scheduled_at: new Date(scheduledAt).toISOString(),
        channels,
        lead_id: isInternal ? null : leadId,
        entity_type: isInternal ? null : "lead",
        entity_id: isInternal ? null : leadId,
        template_id: template?.id || null,
      });
      toast.success("Task created");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fu-create-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-bold text-[#1a3c5e]">
            New {isInternal ? "internal task" : "customer follow-up"}
            {sug && <span className="ml-2 text-[10px] font-normal text-purple-600 italic">(from AI suggestion)</span>}
          </h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">Type *</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {allTypes.map(t => {
                const I = t.Icon;
                return (
                  <button key={t.id} onClick={() => setTaskType(t.id)} data-testid={`fu-create-type-${t.id}`}
                    className={`px-2 py-1 text-[10px] font-bold rounded-full inline-flex items-center gap-1 border ${taskType === t.id ? "bg-[#1a3c5e] text-white border-[#1a3c5e]" : "bg-white border-gray-200 text-gray-700"}`}>
                    <I className="w-2.5 h-2.5" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-600">Priority *</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} data-testid="fu-create-priority"
                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm">
                {Object.keys(PRIORITY_META).map(p => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-600">Scheduled at *</label>
              <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                data-testid="fu-create-scheduled"
                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
            </div>
          </div>
          {!isInternal && (
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-600">Lead *</label>
              <select value={leadId} onChange={e => setLeadId(e.target.value)} data-testid="fu-create-lead"
                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm">
                <option value="">-- pick a lead --</option>
                {leads.map(l => (
                  <option key={l.id} value={l.id}>
                    {`${l.name || "Unnamed"} · ${l.phone || l.email || ""}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">Title (optional)</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Short title"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">Note</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="What needs doing?" data-testid="fu-create-note"
              className="w-full px-2 py-2 border border-gray-200 rounded text-sm" />
          </div>
          {!isInternal && (
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-600">Channels</label>
              <div className="flex gap-1.5 mt-1">
                {[
                  { id: "in_app", I: Bell, label: "In-App" },
                  { id: "whatsapp", I: MessageCircle, label: "WhatsApp" },
                  { id: "email", I: Mail, label: "Email" },
                ].map(c => {
                  const active = channels.includes(c.id);
                  const I = c.I;
                  return (
                    <button key={c.id} onClick={() => toggleChannel(c.id)}
                      data-testid={`fu-create-ch-${c.id}`}
                      className={`px-2.5 py-1 text-[11px] font-bold rounded-full inline-flex items-center gap-1 border ${active ? "bg-[#1a3c5e] text-white border-[#1a3c5e]" : "bg-white text-gray-600 border-gray-200"}`}>
                      <I className="w-3 h-3" /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="fu-create-save"
            className="px-4 py-1.5 text-xs bg-[#1a3c5e] hover:bg-[#0f2940] text-white font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create
          </button>
        </div>
      </div>
    </div>
  );
}

/* =================== Phase F3 — Calendar view (react-big-calendar) =================== */

function CalendarView({ rows, tab, onSelect, onRefresh }) {
  const events = useMemo(() => rows.map(r => {
    const start = r.scheduled_at ? new Date(r.scheduled_at) : new Date();
    const end = new Date(start.getTime() + 30 * 60 * 1000);  // 30 min slot
    return {
      id: r.id,
      title: r.title || r.task_type || "Task",
      start, end,
      resource: r,
    };
  }), [rows]);

  const eventStyleGetter = (event) => {
    const p = event.resource?.priority || "medium";
    const colorMap = { urgent: "#e11d48", high: "#f97316", medium: "#f59e0b", low: "#10b981" };
    return {
      style: {
        backgroundColor: colorMap[p],
        borderRadius: "4px",
        opacity: event.resource?.status === "done" ? 0.5 : 1,
        color: "white",
        border: "none",
        fontSize: "11px",
        fontWeight: 600,
        padding: "2px 4px",
      },
    };
  };

  const moveEvent = async ({ event, start }) => {
    const fid = event.id;
    if (!window.confirm(`Reschedule "${event.title}" to ${start.toLocaleString()}?`)) return;
    try {
      await api.put(`/sales/follow-ups/${fid}/edit`, { scheduled_at: start.toISOString() });
      toast.success("Rescheduled");
      onRefresh?.();
    } catch (e) {
      // legacy edit endpoint may use a different name — try /schedule
      try {
        await api.post(`/sales/follow-ups/${fid}/schedule`, { scheduled_at: start.toISOString() });
        toast.success("Rescheduled");
        onRefresh?.();
      } catch (e2) {
        toast.error(e2?.response?.data?.detail || "Reschedule failed");
      }
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3" data-testid="fu-calendar">
      <BigCalendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 600 }}
        defaultView="month"
        views={["month", "week", "day", "agenda"]}
        eventPropGetter={eventStyleGetter}
        onSelectEvent={(e) => onSelect?.(e.resource)}
        onEventDrop={moveEvent}
        draggableAccessor={() => true}
        popup
      />
    </div>
  );
}

/* =================== Phase F3 — Reports view =================== */

function ReportsView({ tab }) {
  const [stats, setStats] = useState({ by_status: {}, by_priority: {}, by_type: {}, by_outcome: {}, completion_avg_hrs: 0 });
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/sales/follow-ups/stats"),
        api.get("/sales/follow-ups?status=all"),
      ]);
      const list = (b.data || []).filter(x => (x.kind || "customer") === (tab === "internal" ? "internal" : "customer") ||
                                              (tab === "customer" && !x.kind));
      setRows(list);
      // Aggregate
      const by_priority = {}, by_type = {}, by_outcome = {};
      let totalDone = 0, totalHrs = 0;
      list.forEach(r => {
        const p = r.priority || "medium";
        by_priority[p] = (by_priority[p] || 0) + 1;
        const t = r.task_type || "custom";
        by_type[t] = (by_type[t] || 0) + 1;
        const oc = r.outcome?.code;
        if (oc) by_outcome[oc] = (by_outcome[oc] || 0) + 1;
        if (r.status === "done" && r.created_at && r.updated_at) {
          const diff = (new Date(r.updated_at) - new Date(r.created_at)) / 3600000;
          if (diff > 0 && diff < 720) { totalDone += 1; totalHrs += diff; }
        }
      });
      setStats({ by_status: a.data?.by_status || {}, by_priority, by_type, by_outcome,
                  completion_avg_hrs: totalDone ? +(totalHrs / totalDone).toFixed(1) : 0 });
    } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;

  const total = rows.length;
  const completionRate = total ? Math.round(((stats.by_status.done || 0) / total) * 100) : 0;
  const topType = Object.entries(stats.by_type).sort((a, b) => b[1] - a[1])[0];
  const topOutcome = Object.entries(stats.by_outcome).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="space-y-3" data-testid="fu-reports">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ReportTile label="Total" value={total} Icon={List} />
        <ReportTile label="Completion rate" value={`${completionRate}%`} Icon={CheckCircle} accent="emerald" />
        <ReportTile label="Avg completion" value={`${stats.completion_avg_hrs}h`} Icon={Clock} accent="blue" />
        <ReportTile label="Top type" value={topType ? topType[0].replace(/_/g, " ") : "—"} Icon={TrendingUp} accent="orange" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <BarBlock title="By status"   data={stats.by_status} />
        <BarBlock title="By priority" data={stats.by_priority} />
        <BarBlock title="By outcome"  data={stats.by_outcome} empty="No outcomes recorded yet" />
      </div>
      <BarBlock title="By type" data={stats.by_type} horizontal />
    </div>
  );
}

function ReportTile({ label, value, Icon, accent = "navy" }) {
  const tints = {
    navy: "bg-[#1a3c5e]", emerald: "bg-emerald-500", blue: "bg-blue-500", orange: "bg-[#F7941D]",
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3">
      <div className={`w-9 h-9 ${tints[accent] || tints.navy} rounded-lg flex items-center justify-center text-white`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wide font-bold text-gray-600">{label}</div>
        <div className="text-xl font-bold text-[#1a3c5e]">{value}</div>
      </div>
    </div>
  );
}

function BarBlock({ title, data = {}, empty = "—", horizontal }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = entries.reduce((m, [, v]) => Math.max(m, v), 1);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3">
      <div className="text-[10px] uppercase font-bold text-gray-600 mb-2">{title}</div>
      {entries.length === 0 && <div className="text-xs text-gray-400 italic">{empty}</div>}
      <div className="space-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="text-xs">
            <div className="flex justify-between mb-0.5">
              <span className="text-gray-700">{k.replace(/_/g, " ")}</span>
              <span className="font-bold text-[#1a3c5e]">{v}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#F7941D] rounded-full" style={{ width: `${(v / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
